import React, { useState } from 'react';
import {
  Typography,
  Button,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Psychology,
  AutoAwesome,
  Description,
  ArrowForward,
  ExpandMore,
  Person,
  Star,
  AccountTree,
  Handshake,
  School,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/SEO';

const AITeammatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // FAQ data based on AI teammates/agents research
  const faqData = [
    {
      question: "What are AI teammates and how do they differ from traditional AI tools?",
      answer: "AI teammates are autonomous agents that operate as collaborative partners rather than passive tools. Unlike traditional AI that requires step-by-step guidance, AI teammates can take goals, break them down into steps, adapt to real-time changes, and execute independently. They become true operational partners in your workflow."
    },
    {
      question: "How does the Alex Interview System create custom AI teammates?",
      answer: "Alex, our Managing Executive agent, conducts a natural 10-phase interview to understand your specific needs. Through conversational input, Alex extracts capabilities, interaction styles, collaboration preferences, and constraints to automatically generate tailored agent profiles with optimized system prompts and settings."
    },
    {
      question: "Can AI teammates collaborate with each other?",
      answer: "Yes, Prismatic Forge's AI teammates use the A2A (Agent-to-Agent) Protocol for sophisticated collaboration. They can delegate tasks, share context, coordinate workflows, and learn from each other's expertise. This enables complex multi-agent workflows that exceed what individual agents can accomplish alone."
    },
    {
      question: "What makes 2025 'The Age of Agents' for AI teammates?",
      answer: "2025 represents the Third Wave of AI - moving beyond generative outputs to autonomy, collaboration, and adaptability. AI agents are evolving from tools we use to teammates we collaborate with, capable of autonomous decision-making and proactive problem-solving without constant human guidance."
    },
    {
      question: "How do AI teammates handle trust and coordination challenges?",
      answer: "Prismatic Forge addresses common human-AI collaboration issues through transparent communication, intelligent escalation to humans when needed, and clear role definitions. Our teammates recognize when to transfer conversations seamlessly to humans and maintain audit trails of all decisions and actions."
    },
    {
      question: "What types of business functions can AI teammates handle?",
      answer: "AI teammates can manage entire business functions including customer service, sales support, procurement, productivity coordination, and process automation. They excel at roles requiring consistent execution, pattern recognition, and 24/7 availability while maintaining quality standards."
    },
    {
      question: "How do AI teammates learn and improve over time?",
      answer: "Through our Agent Learning and Improvement System, AI teammates continuously analyze interactions, recognize patterns, and adapt their behavior. They share knowledge with other agents, develop new skills based on successful patterns, and apply machine learning to enhance their effectiveness."
    },
    {
      question: "What security measures protect AI teammate interactions?",
      answer: "All AI teammate communications are encrypted end-to-end with granular access controls and comprehensive audit logging. Privacy boundaries are strictly enforced, ensuring teammates only access information they're authorized to see while maintaining complete transparency in their decision-making processes."
    }
  ];

  const defaultTeammates = [
    {
      name: 'Alex',
      role: 'Managing Executive',
      avatar: '👔',
      color: '#325BA8',
      specialties: [
        'Custom teammate creation via interview',
        'Project management and oversight',
        'Strategic planning and execution',
        'Team coordination and delegation'
      ],
      description: 'Your strategic partner who conducts natural language interviews to create custom teammates tailored to your specific needs.',
      example: 'Alex: "Let\'s create a teammate for your marketing needs. Tell me about your target audience and campaign goals..."'
    },
    {
      name: 'Casey',
      role: 'Task Coordinator',
      avatar: '📋',
      color: '#E94257',
      specialties: [
        'Task clarification and management',
        'Goal and deadline extraction',
        'Team assignment coordination',
        'Progress tracking and updates'
      ],
      description: 'Your productivity specialist who ensures every task has clear goals, deadlines, and deliverables.',
      example: 'Casey: "I understand you need to prepare a presentation. What\'s the deadline, who\'s the audience, and what key points should we cover?"'
    },
    {
      name: 'Maya',
      role: 'Communication Specialist',
      avatar: '📧',
      color: '#4DC4D2',
      specialties: [
        'Email analysis and insights',
        'Communication pattern recognition',
        'Relationship mapping',
        'Context extraction from conversations'
      ],
      description: 'Your communication intelligence expert who understands your email patterns and relationship dynamics.',
      example: 'Maya: "I noticed 3 email threads about the Q4 launch. Here are the key concerns from each stakeholder group..."'
    },
    {
      name: 'Sam',
      role: 'Document Intelligence',
      avatar: '📚',
      color: '#4DC4D2',
      specialties: [
        'Document analysis and synthesis',
        'Knowledge extraction and organization',
        'Research and insights generation',
        'Cross-document pattern recognition'
      ],
      description: 'Your knowledge specialist who transforms documents into actionable insights and discoverable knowledge.',
      example: 'Sam: "Based on the quarterly reports, I\'ve identified 3 market trends that impact your strategy. Here\'s the analysis..."'
    },
    {
      name: 'Jordan',
      role: 'Quality & Critique',
      avatar: '🎯',
      color: '#892987',
      specialties: [
        'Critical analysis and feedback',
        'Quality assurance and improvement',
        'Risk identification and mitigation',
        'Performance optimization suggestions'
      ],
      description: 'Your quality partner who provides constructive critique and helps improve outcomes through rigorous analysis.',
      example: 'Jordan: "I\'ve reviewed your proposal. The technical approach is solid, but consider these 3 potential risks and mitigation strategies..."'
    }
  ];

  const interviewSteps = [
    {
      phase: 'Introduction & Setup',
      description: 'Alex welcomes you and explains the teammate creation process',
      example: 'Alex: "I\'m excited to help you create a custom teammate! This conversation will help me understand exactly what kind of AI partner would be most valuable for your work."'
    },
    {
      phase: 'Naming & Identity',
      description: 'Choose your teammate\'s name and establish their core identity',
      example: 'Alex: "What would you like to call your new teammate? And how do you envision their personality - formal and analytical, or more creative and conversational?"'
    },
    {
      phase: 'Purpose & Role Definition',
      description: 'Define the primary role and objectives for your custom teammate',
      example: 'Alex: "Tell me about the main challenges this teammate should help you solve. What specific role will they play in your daily work?"'
    },
    {
      phase: 'Capabilities & Skills',
      description: 'Identify the specific skills and functions your teammate needs',
      example: 'Alex: "What specific tasks should they excel at? Should they focus on data analysis, creative writing, project coordination, or something else entirely?"'
    },
    {
      phase: 'Interaction Style',
      description: 'Set communication preferences and personality traits',
      example: 'Alex: "How should they communicate with you? Do you prefer detailed explanations or concise summaries? Formal tone or casual conversation?"'
    },
    {
      phase: 'Constraints & Boundaries',
      description: 'Establish limitations and ethical guidelines',
      example: 'Alex: "Are there any topics they should avoid or specific boundaries they should respect? Any particular approaches they shouldn\'t take?"'
    },
    {
      phase: 'Collaboration Preferences',
      description: 'Configure how they work with other teammates',
      example: 'Alex: "How should they collaborate with our existing team? Should they delegate certain tasks to Sam, Casey, or others?"'
    },
    {
      phase: 'Review & Validation',
      description: 'Confirm all collected information and make final adjustments',
      example: 'Alex: "Let me summarize what we\'ve designed together. Your new teammate will be [summary]. Does this match your vision?"'
    },
    {
      phase: 'Testing & Refinement',
      description: 'Test the teammate with real scenarios and refine as needed',
      example: 'Alex: "Let\'s test them with a few scenarios. Try asking them about [relevant task]. How does their response feel?"'
    },
    {
      phase: 'Deployment & Integration',
      description: 'Activate your new teammate and integrate them into your workflow',
      example: 'Alex: "Perfect! Your new teammate is ready. They\'re now available in your chat and will appear in your teammate network."'
    }
  ];

  const collaborationFeatures = [
    {
      icon: <Handshake style={{ fontSize: 40, color: '#325BA8' }} />,
      title: 'A2A Protocol',
      description: 'Teammates communicate directly with each other using our Agent-to-Agent protocol for seamless collaboration.',
      benefits: [
        'Direct teammate-to-teammate communication',
        'Automatic task handoffs',
        'Context sharing across teammates',
        'Parallel workflow execution'
      ]
    },
    {
      icon: <AccountTree style={{ fontSize: 40, color: '#4DC4D2' }} />,
      title: 'Multi-Teammate Workflows',
      description: 'Create sophisticated workflows where multiple teammates collaborate on complex tasks.',
      benefits: [
        'Sam→Jordan→Sam quality loops',
        'Casey→Custom teammate delegation',
        'Maya→Sam knowledge synthesis',
        'Real-time collaboration monitoring'
      ]
    },
    {
      icon: <School style={{ fontSize: 40, color: '#E94257' }} />,
      title: 'Continuous Learning',
      description: 'Teammates learn from each interaction and improve their performance over time.',
      benefits: [
        'Performance analytics and improvement',
        'User feedback integration',
        'Skill development tracking',
        'Knowledge sharing between teammates'
      ]
    }
  ];

  const customExamples = [
    {
      title: 'Marketing Strategist',
      conversation: [
        'You: "I need a teammate focused on marketing strategy"',
        'Alex: "Excellent! Tell me about your target market and current marketing challenges."',
        'You: "We\'re targeting B2B SaaS companies, struggling with lead generation"',
        'Alex: "Perfect. Should they focus on content strategy, campaign analysis, or both?"',
        'You: "Both, plus they should help with competitor research"',
        'Alex: "Great! I\'ll create a marketing strategist who specializes in B2B SaaS lead generation, content strategy, campaign optimization, and competitive intelligence."'
      ],
      result: 'Created: Marketing Maven - specializes in B2B SaaS marketing with focus on lead generation and competitive analysis'
    },
    {
      title: 'Technical Writer',
      conversation: [
        'You: "I need help with technical documentation"',
        'Alex: "What kind of technical content do you work with most often?"',
        'You: "API documentation, user guides, and engineering specs"',
        'Alex: "Should they have any specific technical background or industry focus?"',
        'You: "Yes, they should understand software development and be familiar with REST APIs"',
        'Alex: "Perfect! I\'ll create a technical writing specialist with software development expertise."'
      ],
      result: 'Created: DocuMentor - technical writing expert with software development background, specializing in API docs and user guides'
    },
    {
      title: 'Research Analyst',
      conversation: [
        'You: "I need someone who can help with market research and data analysis"',
        'Alex: "What industries or types of research do you focus on?"',
        'You: "Financial markets and investment research, lots of data analysis"',
        'Alex: "Should they be more quantitative or qualitative in their approach?"',
        'You: "Definitely quantitative, with strong statistical analysis skills"',
        'Alex: "Excellent! I\'ll create a quantitative research specialist for financial markets."'
      ],
      result: 'Created: DataSage - quantitative research analyst specializing in financial markets with advanced statistical analysis capabilities'
    }
  ];

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Prismatic Forge AI Teammates",
    "description": "Collaborative AI agents that work as autonomous teammates. Create custom AI agents through natural conversation and enable sophisticated agent-to-agent collaboration.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "29",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "RecurringPriceSpecification",
        "billingFrequency": "monthly"
      }
    },
    "provider": {
      "@type": "Organization",
      "name": "Prismatic Forge"
    }
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#fafafa',
      overflowX: 'hidden',
      overflowY: 'auto',
      paddingLeft: 16,
      paddingRight: 16,
    }}>
      <SEOHead
        title="AI Teammates - Collaborative Autonomous Agents"
        description="Experience the future of AI collaboration with autonomous teammates. Create custom AI agents through conversation, enable agent-to-agent workflows, and transform how your team works."
        keywords="AI teammates, autonomous agents, agent collaboration, custom AI agents, agentic AI, multi-agent systems, AI workforce, agent-to-agent protocol"
        ogTitle="AI Teammates - Prismatic Forge"
        ogDescription="Revolutionary AI teammates that work autonomously and collaborate with each other. Create custom agents through natural conversation and unlock the power of multi-agent workflows."
        canonical="https://prismaticforge.ai/features/ai-teammates"
        jsonLd={structuredData}
      />
      {/* Hero Section */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0C1319 0%, #325BA8 100%)',
          color: 'white',
          paddingTop: 48,
          paddingBottom: 48,
          position: 'relative',
          overflow: 'hidden',
          marginLeft: -16,
          marginRight: -16,
        }}
      >
        <div style={{
          paddingLeft: 16,
          paddingRight: 16,
        }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                style={{
                  fontWeight: 'bold',
                  lineHeight: 1.1,
                  marginBottom: 24,
                }}
              >
                Meet Your{' '}
                <span style={{ color: '#4DC4D2' }}>
                  AI Teammates
                </span>
              </Typography>
              <Typography
                variant="h5"
                style={{
                  marginBottom: 32,
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 300,
                  lineHeight: 1.4,
                }}
              >
                Five specialized default teammates ready to help, plus Alex's revolutionary interview system
                to create custom teammates tailored to your exact needs.
              </Typography>
              <Stack direction="row" spacing={2} style={{ marginBottom: 32 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup')}
                  style={{
                    backgroundColor: '#E94257',
                    color: 'white',
                    fontWeight: 'bold',
                    paddingLeft: 32,
                    paddingRight: 32,
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                  endIcon={<ArrowForward />}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  style={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    paddingLeft: 32,
                    paddingRight: 32,
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                >
                  Login
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <List style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <CheckCircle style={{ color: '#4DC4D2' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Alex - Managing Executive for team creation"
                    primaryTypographyProps={{ fontSize: '1.1rem' }}
                  />
                </ListItem>
                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <CheckCircle style={{ color: '#4DC4D2' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Maya - Email specialist for communication insights"
                    primaryTypographyProps={{ fontSize: '1.1rem' }}
                  />
                </ListItem>
                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <CheckCircle style={{ color: '#4DC4D2' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sam - Document specialist for knowledge extraction"
                    primaryTypographyProps={{ fontSize: '1.1rem' }}
                  />
                </ListItem>
                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <CheckCircle style={{ color: '#4DC4D2' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Jordan - Critique specialist for quality improvement"
                    primaryTypographyProps={{ fontSize: '1.1rem' }}
                  />
                </ListItem>
                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <CheckCircle style={{ color: '#4DC4D2' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Casey - Daily awareness for project updates"
                    primaryTypographyProps={{ fontSize: '1.1rem' }}
                  />
                </ListItem>
                <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <CheckCircle style={{ color: '#4DC4D2' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Unlimited custom teammates via Alex's interview system"
                    primaryTypographyProps={{ fontSize: '1.1rem', fontStyle: 'italic' }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Default Teammates Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80, maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          style={{ marginBottom: 64, fontWeight: 'bold', color: '#325BA8' }}
        >
          Your Default AI Teammates
        </Typography>
        <Grid container spacing={4}>
          {defaultTeammates.map((teammate, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                style={{
                  height: '100%',
                  padding: 24,
                  border: `1px solid ${teammate.color}30`,
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <Typography style={{ fontSize: '2rem', marginRight: 16 }}>
                    {teammate.avatar}
                  </Typography>
                  <div>
                    <Typography variant="h5" style={{ fontWeight: 'bold', color: teammate.color }}>
                      {teammate.name}
                    </Typography>
                    <Typography variant="subtitle1" style={{ color: 'rgba(0,0,0,0.6)' }}>
                      {teammate.role}
                    </Typography>
                  </div>
                </div>
                <Typography variant="body1" style={{ marginBottom: 24, color: 'rgba(0,0,0,0.6)' }}>
                  {teammate.description}
                </Typography>
                <List dense>
                  {teammate.specialties.map((specialty, i) => (
                    <ListItem key={i} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <CheckCircle style={{ fontSize: 16, color: teammate.color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={specialty}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Paper style={{ padding: 16, marginTop: 16, backgroundColor: `${teammate.color}10` }}>
                  <Typography variant="body2" style={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                    {teammate.example}
                  </Typography>
                </Paper>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Alex Interview System */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div style={{ maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
          >
            Alex's Revolutionary Interview System
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            style={{ marginBottom: 64, color: 'rgba(0,0,0,0.6)', maxWidth: 800, marginLeft: 'auto', marginRight: 'auto' }}
          >
            Alex conducts natural language interviews to understand your needs and automatically
            creates custom teammates with the exact capabilities, personality, and expertise you require.
          </Typography>

          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', marginBottom: 24 }}>
                10-Phase Interview Process
              </Typography>
              <Stepper orientation="vertical" activeStep={activeStep}>
                {interviewSteps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel
                      onClick={() => setActiveStep(index)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                        {step.phase}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 16 }}>
                        {step.description}
                      </Typography>
                      <Paper style={{ padding: 16, backgroundColor: 'rgba(50, 91, 168, 0.05)' }}>
                        <Typography variant="body2" style={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                          {step.example}
                        </Typography>
                      </Paper>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card style={{ padding: 32, height: 'fit-content', border: '1px solid rgba(50, 91, 168, 0.2)' }}>
                <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', color: '#325BA8' }}>
                  What Alex Creates
                </Typography>
                <List>
                  {[
                    'Optimized system prompts based on your requirements',
                    'Custom personality and communication style',
                    'Specialized skills and knowledge domains',
                    'Collaboration preferences with other teammates',
                    'Ethical boundaries and operational constraints',
                    'Performance metrics and success criteria'
                  ].map((feature, index) => (
                    <ListItem key={index} style={{ paddingLeft: 0 }}>
                      <ListItemIcon>
                        <Star style={{ color: '#325BA8' }} />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Custom Teammate Examples */}
      <div style={{ paddingTop: 80, paddingBottom: 80, maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
        >
          Custom Teammate Creation Examples
        </Typography>
        <Grid container spacing={4}>
          {customExamples.map((example, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                style={{
                  height: '100%',
                  padding: 24,
                  border: '1px solid rgba(50, 91, 168, 0.2)',
                }}
              >
                <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', color: '#325BA8' }}>
                  {example.title}
                </Typography>
                <div style={{ marginBottom: 24 }}>
                  {example.conversation.map((line, i) => (
                    <Typography
                      key={i}
                      variant="body2"
                      style={{
                        marginBottom: 8,
                        fontSize: '0.875rem',
                        fontStyle: line.startsWith('Alex:') ? 'italic' : 'normal',
                        color: line.startsWith('Alex:') ? '#325BA8' : 'rgba(0,0,0,0.87)',
                        fontWeight: line.startsWith('You:') ? 'bold' : 'normal',
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </div>
                <Paper style={{ padding: 16, backgroundColor: 'rgba(76, 196, 210, 0.05)' }}>
                  <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4DC4D2', marginBottom: 8 }}>
                    Result:
                  </Typography>
                  <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                    {example.result}
                  </Typography>
                </Paper>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Collaboration Features */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div style={{ maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
          >
            Advanced Teammate Collaboration
          </Typography>
          <Grid container spacing={4}>
            {collaborationFeatures.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  style={{
                    height: '100%',
                    padding: 24,
                    textAlign: 'center',
                    border: '1px solid rgba(76, 196, 210, 0.2)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div style={{ marginBottom: 16 }}>
                    {feature.icon}
                  </div>
                  <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 24 }}>
                    {feature.description}
                  </Typography>
                  <List dense>
                    {feature.benefits.map((benefit, i) => (
                      <ListItem key={i} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                        <ListItemIcon style={{ minWidth: 32, justifyContent: 'center' }}>
                          <CheckCircle style={{ fontSize: 16, color: '#4DC4D2' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={benefit}
                          primaryTypographyProps={{ fontSize: '0.875rem', textAlign: 'left' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>

      {/* Workflow Example */}
      <div style={{ paddingTop: 80, paddingBottom: 80, maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
        >
          Multi-Teammate Workflow Example
        </Typography>
        <Card style={{ padding: 32, border: '1px solid rgba(76, 196, 210, 0.2)' }}>
          <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', marginBottom: 24 }}>
            Content Creation & Review Workflow
          </Typography>
          <div>
            {[
              { color: '#4DC4D2', icon: <Description />, title: 'Sam: Research & Draft Creation', desc: 'Sam researches the topic, analyzes relevant documents, and creates the initial draft based on your requirements.', hasConnector: true },
              { color: '#892987', icon: <Psychology />, title: 'Jordan: Critical Review & Feedback', desc: 'Jordan reviews Sam\'s draft, identifies areas for improvement, and provides detailed feedback on structure, arguments, and clarity.', hasConnector: true },
              { color: '#4DC4D2', icon: <AutoAwesome />, title: 'Sam: Refinement & Polish', desc: 'Sam incorporates Jordan\'s feedback, refines the content, and creates the final polished version.', hasConnector: true },
              { color: '#325BA8', icon: <Person />, title: 'You: Final Review & Approval', desc: 'You receive the final version with a summary of all changes and improvements made during the collaborative process.', hasConnector: false },
            ].map((step, index) => (
              <div key={index} style={{ display: 'flex', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '50%', backgroundColor: step.color, color: 'white' }}>
                    {step.icon}
                  </div>
                  {step.hasConnector && (
                    <div style={{ width: 2, flexGrow: 1, backgroundColor: '#e0e0e0', minHeight: 24 }} />
                  )}
                </div>
                <div style={{ paddingBottom: step.hasConnector ? 24 : 0 }}>
                  <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
                    {step.desc}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* CTA Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center', maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' }}>
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          style={{ fontWeight: 'bold', marginBottom: 24 }}
        >
          Ready to Meet Your AI Teammates?
        </Typography>
        <Typography
          variant="h6"
          style={{ marginBottom: 32, color: 'rgba(0,0,0,0.6)', fontWeight: 300, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}
        >
          Experience the power of AI collaboration. Start with our five specialized teammates
          and create unlimited custom teammates through Alex's interview system.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            style={{
              backgroundColor: '#E94257',
              color: 'white',
              fontWeight: 'bold',
              paddingLeft: 48,
              paddingRight: 48,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: '1.1rem',
            }}
            endIcon={<ArrowForward />}
          >
            Start Your Free Trial
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/chat')}
            style={{
              borderColor: '#E94257',
              color: '#E94257',
              fontWeight: 'bold',
              paddingLeft: 48,
              paddingRight: 48,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: '1.1rem',
            }}
          >
            Meet the Team
          </Button>
        </Stack>
        <Typography variant="body2" style={{ marginTop: 24, color: 'rgba(0,0,0,0.6)' }}>
          30-day free trial • No credit card required • Cancel anytime
        </Typography>
      </div>

      {/* FAQ Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80, maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' }}>
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          style={{ fontWeight: 'bold', marginBottom: 48, textAlign: 'center' }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography
          variant="h6"
          style={{ marginBottom: 48, color: 'rgba(0,0,0,0.6)', textAlign: 'center', maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}
        >
          Everything you need to know about AI teammates and autonomous agents
        </Typography>

        <Grid container spacing={3}>
          {faqData.map((faq, index) => (
            <Grid item xs={12} key={index}>
              <Accordion
                style={{
                  backgroundColor: '#fff',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography variant="h6" style={{ fontWeight: 500 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ paddingTop: 0, paddingBottom: 24 }}>
                  <Typography variant="body1" style={{ color: 'rgba(0,0,0,0.6)', lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Footer */}
      <div style={{ marginLeft: -16, marginRight: -16 }}>
        <Divider />
      </div>
      <div style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
            © 2025 Prismatic Forge. All rights reserved.
          </Typography>
          <div style={{
            display: 'flex',
            gap: 24,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <Typography
              component="a"
              href="/terms"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Terms of Service
            </Typography>
            <Typography
              component="a"
              href="/privacy/"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              component="a"
              href="/docs"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Documentation
            </Typography>
            <Typography
              component="a"
              href="mailto:support@prismaticforge.ai"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Contact Support
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITeammatesPage;
